import React, { useState } from 'react';
import { timeAgo } from '../../utils/utils';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const { Box, Chip, Button, Typography, MenuItem, TextField, FormControl, InputLabel, Select } = require('@mui/material');

const Filters = ({ 
	startDate, setStartDate, 
	endDate, setEndDate, 
	platforms = null, 
	selectedPlatforms = [], 
	setSelectedPlatforms = [],
	clickIds = null,
	clickId = null, 
	setClickId = null,
	onReload,
	lastUpdate
}) => {
	const { t } = useTranslation();
	const [selectedQuickFilter, setSelectedQuickFilter] = useState(null);

	const handleQuickFilter = (range) => {
		const today = dayjs();
		let start;

		switch (range) {
			case '1D':
				start = today.subtract(1, 'day');
				break;
			case '1W':
				start = today.subtract(1, 'week');
				break;
			case '1M':
				start = today.subtract(1, 'month');
				break;
			case '6M':
				start = today.subtract(6, 'month');
				break;
			case '1Y':
				start = today.subtract(1, 'year');
				break;
			default:
				start = today;
		}

		setSelectedQuickFilter(range);
		setStartDate(start.format('YYYY-MM-DD'));
		setEndDate(today.format('YYYY-MM-DD'));
		onReload();
	};

	return (
		<Box padding={2} sx={{ width: '100%' }}>
			<Box 
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: { xs: 'center', md: 'center' },
					gap: 1,
					width: '100%',
					justifyContent: { xs: 'center', md: 'flex-start' },
					textAlign: { xs: 'center', md: 'left' },
				}}
			>
				<Box sx={{
					textAlign: { xs: 'center', md: 'left' },
					flexDirection: { xs: 'column', md: 'row' },
				}}>
					<TextField
						type="date"
						label={t('filters.startDate')}
						value={startDate}
						size="small"
						sx={{ marginRight: 1, width: { xs: '40vw', md: 'auto' } }}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				
					<TextField
						type="date"
						label={t('filters.endDate')}
						value={endDate}
						size="small"
						sx={{ width: { xs: '40vw', md: 'auto' } }}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</Box>
			
				<Box sx={{ textAlign: { xs: 'center', md: 'left' }, width: { xs: 'auto', md: 'auto' } }}>
				{platforms?.length > 0 &&
					<FormControl 
						size="small" 
						sx={{ width: { xs: '40vw', sm: '40vw', md: '200px' }, marginRight: 1 }}
					>
						<InputLabel id="platform-select-label">{t('filters.platform')}</InputLabel>
						<Select
							labelId="platform-select-label"
							value={selectedPlatforms}
							multiple={true}
							onChange={(e) => {
								const value = e.target.value;
								if (value.includes("all")) {
									setSelectedPlatforms([]);
								} else {
									setSelectedPlatforms(value);
								}
							}}
							renderValue={(selected) =>
								selected.length === 0 || selected.includes("all")
									? t('filters.all')
									: selected.join(", ")
							}
						>
							<MenuItem key="all" value="all">
								<em>{t('filters.all')}</em>
							</MenuItem>
							{platforms?.map((platform) => (
								<MenuItem key={platform.id} value={platform.id}>
									{platform.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				}

				{clickIds?.length > 0 &&
					<FormControl 
						size="small"
						sx={{ width: { xs: '40vw', sm: '40vw', md: '200px' }, marginBottom: { xs: 2, md: 0 } }}
					>
						<InputLabel id="clickId-select-label">Click ID</InputLabel>
						<Select
							labelId="clickId-select-label"
							value={clickId}
							onChange={(e) => setClickId(e.target.value)}
							renderValue={(selected) =>
								selected.length === 0 ? t('filters.all') : selected
							}
						>
							<MenuItem value="">
								<em>{t('filters.all')}</em>
							</MenuItem>
							{clickIds.map((clickId) => (
								<MenuItem key={clickId} value={clickId}>
									{clickId}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				}

				{/* Quick Filters (Mobile) */}
				<Box
					sx={{ display: { xs: 'flex', md: 'none' } }}
					justifyContent={{ xs: 'center', md: 'flex-start' }}
					alignItems="center"
					gap={1}
					flexWrap="wrap"
				>
					{['1D', '1W', '1M', '6M', '1Y'].map((range) => (
						<Chip
							key={range}
							label={range}
							variant={selectedQuickFilter === range ? "contained" : "outlined"}
							size="small"
							sx={{ minWidth: 48 }}
							onClick={() => handleQuickFilter(range)}
						/>
					))}
				</Box>
				</Box>

				<Button 
					sx={{ 
						marginLeft: { xs: 0, md: 1 },
						marginBottom: { xs: 1, md: 0 },
						width: { xs: '90%', sm: '84%', md: 'auto' },
					}}
					variant="contained" 
					onClick={onReload}
				>
					{t('filters.refresh')}
				</Button>

				<Typography 
					variant="body2"
					sx={{ color: '#555', marginLeft: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}
				>
					{t('filters.lastUpdate', { time: timeAgo(lastUpdate) })}
				</Typography>
			</Box>

			{/* Quick Filters (Desktop) */}
			<Box
				sx={{ display: { xs: 'none', md: 'flex' } }}
				justifyContent={{ xs: 'center', md: 'flex-start' }}
				alignItems="center"
				gap={1}
				marginTop={2}
				flexWrap="wrap"
			>
				{['1D', '1W', '1M', '6M', '1Y'].map((range) => (
					<Chip
						key={range}
						label={range}
						variant={selectedQuickFilter === range ? "contained" : "outlined"}
						sx={{ minWidth: 48 }}
						onClick={() => handleQuickFilter(range)}
					/>
				))}
			</Box>
		</Box>
	);
};

export default Filters;